/**
 * Primitives shared by the paywall funnel's screens.
 *
 * The app surface already owns the design system (`app/tokens.ts`,
 * `app/primitives.tsx`, `app/orb.tsx`) and the funnel reuses it wholesale —
 * same tokens, same Orb, same zone scale — so the screens a person sees before
 * paying and the ones they see after are visibly the same product.
 *
 * What lives here is only what the funnel adds: the serif section headings, the
 * two shells (a phone-width column and a 1120px desktop canvas), a real
 * scroll-snap carousel, and the bottom sheet the exit ladder rides on.
 */
import React from 'react';
import { Icon } from '../app/icons';
import { c, hair, hair2, f, ACT } from '../app/tokens';
import { Col, Row, Meta, Muted, usePrefersReducedMotion } from '../app/primitives';
import { useT } from '../i18n/useT';

/** "Free / included / guaranteed" green, from the handoff's paywall token table. */
export const OK = '#4ade80';

/** Desktop breakpoint for the funnel. Below it, the phone column. */
export const WIDE = 900;

/** Desktop content width, centred, per the handoff's `DShell`. */
export const CANVAS = 1120;

export function useWide(): boolean {
    const [wide, setWide] = React.useState(() => typeof window !== 'undefined' && window.innerWidth >= WIDE);

    React.useEffect(() => {
        const on = () => setWide(window.innerWidth >= WIDE);

        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, []);

    return wide;
}

/* ── type ─────────────────────────────────────────────────────────────────── */

export const Serif: React.FC<{
    children: React.ReactNode;
    size?: number;
    italic?: boolean;
    style?: React.CSSProperties;
}> = ({ children, size = 25, italic, style }) => (
    <div
        style={{
            fontFamily: f.serif,
            fontWeight: 300,
            fontStyle: italic ? 'italic' : 'normal',
            fontSize: size,
            lineHeight: 1.18,
            letterSpacing: '-0.022em',
            color: c.textPri,
            ...style,
        }}
    >
        {children}
    </div>
);

/**
 * Render `*asterisk*`-wrapped words in the action colour.
 *
 * The alternative is HTML inside translation strings, which puts markup in the
 * hands of whoever edits copy. One delimiter, no injection.
 */
export const accent = (text: string, color: string = ACT): React.ReactNode => {
    // Split first and carry each chunk's key on the chunk itself. The pieces
    // come from one split of one string and never reorder, so position is a
    // sound identity — but keeping it out of the JSX makes that explicit.
    const chunks = text.split(/\*([^*]+)\*/).map((body, i) => ({ body, key: `c${i}`, hot: i % 2 === 1 }));

    return chunks.map((chunk) =>
        chunk.hot ? (
            <span key={chunk.key} style={{ color }}>
                {chunk.body}
            </span>
        ) : (
            <React.Fragment key={chunk.key}>{chunk.body}</React.Fragment>
        ),
    );
};

/** Centred section head: serif title over an optional muted line. */
export const SectHead: React.FC<{ title: string; sub?: string; wide?: boolean }> = ({ title, sub, wide }) => (
    <Col style={{ maxWidth: wide ? 660 : undefined, margin: '0 auto', textAlign: 'center' }} gap={wide ? 11 : 9}>
        <Serif size={wide ? 34 : 25} style={{ textAlign: 'center' }}>
            {title}
        </Serif>
        {sub && <Muted style={{ fontSize: wide ? 15 : 12.5, lineHeight: 1.5 }}>{sub}</Muted>}
    </Col>
);

/* ── containers ───────────────────────────────────────────────────────────── */

/**
 * One funnel screen.
 *
 * Owns the page background and the phone-width column so no screen has to
 * repeat either. `pad` is the horizontal gutter — 20px on mobile is the
 * handoff's, and the desktop canvas manages its own.
 */
export const Screen: React.FC<{
    children: React.ReactNode;
    wide?: boolean;
    /**
     * Make the content column a full-height flex column, so a screen can pin
     * something to the bottom edge instead of letting it float up behind
     * whatever content happens to be above it.
     */
    column?: boolean;
    style?: React.CSSProperties;
}> = ({ children, wide, column, style }) => (
    <div
        style={{
            background: c.bg,
            color: c.text,
            fontFamily: f.ui,
            minHeight: '100vh',
            ...(column ? { display: 'flex', flexDirection: 'column' } : null),
            ...style,
        }}
    >
        <div
            style={{
                maxWidth: wide ? CANVAS : 430,
                margin: '0 auto',
                width: '100%',
                ...(column ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } : null),
            }}
        >
            {children}
        </div>
    </div>
);

/** Top bar: back affordance on the left, wordmark centred. */
export const TopBar: React.FC<{ onBack?: () => void; right?: React.ReactNode }> = ({ onBack, right }) => (
    <Row style={{ padding: '10px 16px 0', gap: 10 }}>
        {onBack ? (
            <button
                type="button"
                onClick={onBack}
                aria-label="Back"
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    border: hair,
                    background: c.card,
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 auto',
                    cursor: 'pointer',
                    padding: 0,
                }}
            >
                <Icon k="back" size={15} color={c.textPri} />
            </button>
        ) : (
            <div style={{ width: 34, flex: '0 0 auto' }} />
        )}
        <Col style={{ flex: 1, alignItems: 'center' }}>
            <Meta style={{ fontSize: 11, color: c.textPri, letterSpacing: '0.18em' }}>DoYourStep</Meta>
        </Col>
        <div style={{ width: 34, flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end' }}>{right}</div>
    </Row>
);

export const Card: React.FC<{
    children: React.ReactNode;
    accent?: string;
    style?: React.CSSProperties;
}> = ({ children, accent: accentColor, style }) => (
    <div
        style={{
            background: c.card,
            border: accentColor ? `1px solid ${accentColor}` : hair,
            borderRadius: 18,
            padding: '17px 18px 16px',
            ...style,
        }}
    >
        {children}
    </div>
);

/* ── actions ──────────────────────────────────────────────────────────────── */

/**
 * The closing action of a funnel screen, held against the bottom edge.
 *
 * `sticky` rather than `fixed`: it keeps its place in flow, so when the person
 * reaches the end of a long screen the bar simply settles there and the last
 * block is never trapped underneath it. `marginTop: auto` covers the opposite
 * case — content shorter than the fold, where there is nothing to stick to.
 *
 * Between them the button is in the same place whatever the screen holds,
 * which is the whole point: a step's only action should never be something you
 * scroll to find.
 */
export const StickyActions: React.FC<{ children: React.ReactNode; pad?: number }> = ({ children, pad = 20 }) => (
    <div
        style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 20,
            marginTop: 'auto',
            flex: '0 0 auto',
            padding: `16px ${pad}px calc(20px + env(safe-area-inset-bottom, 0px))`,
            background: `linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.94) 30%, ${c.bg} 62%)`,
        }}
    >
        {children}
    </div>
);

export const Cta: React.FC<{
    label: string;
    note?: string;
    onClick?: () => void;
    disabled?: boolean;
    width?: number | string;
    height?: number;
    style?: React.CSSProperties;
}> = ({ label, note, onClick, disabled, width = '100%', height = 50, style }) => (
    <Col style={{ alignItems: 'center', width: '100%', ...style }} gap={12}>
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                width,
                height,
                borderRadius: 99,
                border: 'none',
                cursor: disabled ? 'default' : 'pointer',
                background: disabled ? 'rgba(232,230,224,0.06)' : c.textPri,
                color: disabled ? 'rgba(232,230,224,0.32)' : '#0a0a0a',
                fontFamily: f.ui,
                fontSize: 15,
                fontWeight: 500,
                letterSpacing: '-0.01em',
                transition: 'background 220ms, color 220ms',
            }}
        >
            {label}
            {!disabled && <Icon k="arrow" size={16} color="#0a0a0a" />}
        </button>
        {note && (
            <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.1em', textAlign: 'center' }}>{note}</Meta>
        )}
    </Col>
);

/**
 * The soft way out.
 *
 * Kept at `c.muted` deliberately: the handoff's rule is that "leave anyway" is
 * never dimmer than ordinary secondary text. A decline the eye cannot find is
 * not a decline the user actually consented to.
 */
export const SoftLink: React.FC<{ label: string; onClick?: () => void }> = ({ label, onClick }) => (
    <Row style={{ justifyContent: 'center', marginTop: 16 }}>
        <button
            type="button"
            onClick={onClick}
            style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: f.ui,
                fontSize: 13.5,
                fontWeight: 400,
                color: c.muted,
                borderBottom: '1px solid rgba(232,230,224,0.2)',
                paddingBottom: 2,
            }}
        >
            {label}
        </button>
    </Row>
);

/* ── carousel ─────────────────────────────────────────────────────────────── */

export const Dots: React.FC<{ n: number; active: number; onPick?: (i: number) => void }> = ({ n, active, onPick }) => (
    <Row style={{ justifyContent: 'center', gap: 6 }}>
        {Array.from({ length: n }, (_, i) => (
            <button
                key={`dot-${i}`}
                type="button"
                aria-label={`${i + 1} / ${n}`}
                onClick={() => onPick?.(i)}
                style={{
                    width: i === active ? 18 : 6,
                    height: 6,
                    borderRadius: 3,
                    border: 'none',
                    padding: 0,
                    cursor: onPick ? 'pointer' : 'default',
                    background: i === active ? c.textPri : 'rgba(232,230,224,0.2)',
                    transition: 'width 260ms, background 260ms',
                }}
            />
        ))}
    </Row>
);

/**
 * Scroll-snap carousel.
 *
 * A real overflow container rather than a transform track: touch, trackpad and
 * keyboard all work without a gesture library, and the neighbouring card peeks
 * by itself because the slide is narrower than the viewport. The dots read the
 * scroll position, so they cannot disagree with what is on screen.
 *
 * `card` is the slide width; leave it undefined to let each slide fill the
 * container.
 */
export const Carousel: React.FC<{
    children: React.ReactNode[];
    card?: number;
    /**
     * Space between slides. For a peeking carousel it defaults to the gutter:
     * with a smaller gap the previous slide's edge pokes into the gutter and
     * reads as a card cut off by the screen, rather than as one tucked away.
     */
    gap?: number;
    pad?: number;
    /** Show prev/next buttons — pointer devices only, touch has the gesture. */
    arrows?: boolean;
    /**
     * Advance on its own every N ms, wrapping from the last slide back to the
     * first. Stops for good the moment the person takes over.
     */
    auto?: number;
}> = ({ children, card, gap, pad = 20, arrows, auto }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [active, setActive] = React.useState(0);
    const step = gap ?? (card ? pad : 12);
    const reduced = usePrefersReducedMotion();
    // A carousel that keeps moving after someone has grabbed it is fighting
    // them. One deliberate interaction hands control over permanently.
    const [taken, setTaken] = React.useState(false);
    const n = children.length;

    const onScroll = React.useCallback(() => {
        const el = ref.current;

        if (!el) return;
        const slide = el.firstElementChild as HTMLElement | null;
        const slot = slide ? slide.offsetWidth + step : el.clientWidth;

        setActive(Math.max(0, Math.min(n - 1, Math.round(el.scrollLeft / slot))));
    }, [step, n]);

    const goTo = React.useCallback(
        (i: number) => {
            const el = ref.current;

            if (!el) return;
            const slide = el.children[i] as HTMLElement | undefined;

            if (slide) el.scrollTo({ left: slide.offsetLeft - pad, behavior: 'smooth' });
        },
        [pad],
    );

    const take = React.useCallback(() => setTaken(true), []);

    React.useEffect(() => {
        if (!auto || taken || reduced || n < 2) return undefined;

        // Read `active` off the DOM rather than closing over it, so the timer is
        // set up once instead of being torn down on every advance.
        const id = setInterval(() => {
            const el = ref.current;

            if (!el || document.hidden) return;
            const slide = el.firstElementChild as HTMLElement | null;
            const slot = slide ? slide.offsetWidth + step : el.clientWidth;
            const at = Math.round(el.scrollLeft / slot);

            goTo(at >= n - 1 ? 0 : at + 1);
        }, auto);

        return () => clearInterval(id);
    }, [auto, taken, reduced, n, step, goTo]);

    return (
        <Col gap={16}>
            <div
                style={{ position: 'relative' }}
                onPointerDown={take}
                onWheel={take}
                onKeyDown={take}
                role="presentation"
            >
                <div
                    ref={ref}
                    onScroll={onScroll}
                    style={{
                        display: 'flex',
                        gap: step,
                        padding: `0 ${pad}px`,
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        // Without this, `scroll-snap-align: start` snaps each
                        // slide flush to the container edge and eats the gutter,
                        // so every card sits `pad` px left of the page's other
                        // content — the "shifted carousel" everyone notices.
                        scrollPaddingLeft: pad,
                        // The scrollbar would sit under the cards on desktop and
                        // read as a stray rule; the dots are the affordance.
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        WebkitOverflowScrolling: 'touch',
                        alignItems: 'stretch',
                    }}
                >
                    {children.map((child, i) => (
                        <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={`slide-${i}`}
                            style={{
                                flex: card ? `0 0 ${card}px` : '0 0 100%',
                                maxWidth: card || '100%',
                                scrollSnapAlign: 'start',
                                display: 'flex',
                            }}
                        >
                            {child}
                        </div>
                    ))}
                    {/*
                     * Tail spacer, only for peeking carousels.
                     *
                     * A card narrower than the track cannot reach the left
                     * gutter on the last slide — the scroll runs out first — so
                     * it settles mid-track with the PREVIOUS card hanging into
                     * view on the left, which reads as a broken layout rather
                     * than as a peek. This gives the scroll exactly the extra
                     * run it needs, so every slide including the last lands on
                     * the gutter. `max(0px, …)` keeps it inert when the card is
                     * already as wide as the track.
                     *
                     * The width is `track − card − pad`, and a percentage here
                     * resolves against the flex container's CONTENT box, which
                     * is already `track − 2·pad` — hence `100% + pad − card`.
                     * Subtracting `pad` instead leaves the last slide a gutter's
                     * width short, which is the sliver it was meant to remove.
                     */}
                    {card && (
                        <div
                            aria-hidden
                            style={{ flex: '0 0 auto', width: `max(0px, calc(100% + ${pad}px - ${card}px))` }}
                        />
                    )}
                </div>
                {arrows && n > 1 && (
                    <>
                        <CarouselArrow dir="prev" disabled={active === 0} onClick={() => goTo(active - 1)} />
                        <CarouselArrow dir="next" disabled={active === n - 1} onClick={() => goTo(active + 1)} />
                    </>
                )}
            </div>
            {n > 1 && (
                <Dots
                    n={n}
                    active={active}
                    onPick={(i) => {
                        take();
                        goTo(i);
                    }}
                />
            )}
        </Col>
    );
};

const CarouselArrow: React.FC<{ dir: 'prev' | 'next'; disabled: boolean; onClick: () => void }> = ({
    dir,
    disabled,
    onClick,
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={dir}
        style={{
            position: 'absolute',
            top: '50%',
            // Outside the track, not on top of it: an arrow overlapping the
            // first card's text is a button that hides the thing it points at.
            [dir === 'prev' ? 'left' : 'right']: -54,
            transform: 'translateY(-50%)',
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: hair,
            background: 'rgba(10,10,10,0.86)',
            backdropFilter: 'blur(10px)',
            display: 'grid',
            placeItems: 'center',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.25 : 1,
            transition: 'opacity 200ms',
            zIndex: 2,
        }}
    >
        <Icon k={dir === 'prev' ? 'back' : 'chev'} size={16} color={c.textPri} />
    </button>
);

/* ── review card ──────────────────────────────────────────────────────────── */

export const Stars: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <Row style={{ gap: 3.5 }} aria-label="5 out of 5">
        {[0, 1, 2, 3, 4].map((i) => (
            <Icon key={`star-${i}`} k="star" size={size} color={ACT} />
        ))}
    </Row>
);

export interface Review {
    title: string;
    quote: string;
    name: string;
    age: number;
    /** What they do. Empty when none was given. */
    role?: string;
}

/**
 * A testimonial.
 *
 * `clamp` cuts the quote to a few lines behind a "more" link. Real reviews run
 * long, and on a phone one of them can swallow the whole screen — including the
 * step's own button. The full text is one tap away and nothing is truncated in
 * the DOM, so it stays selectable and searchable.
 */
export const ReviewCard: React.FC<{ review: Review; wide?: boolean; clamp?: number }> = ({
    review,
    wide,
    clamp,
}) => {
    const t = useT();
    const [open, setOpen] = React.useState(false);
    const cut = !!clamp && !open;

    return (
        <Col
            style={{
                width: '100%',
                padding: wide ? '24px 26px 20px' : '17px 18px 15px',
                borderRadius: 18,
                background: `linear-gradient(165deg, rgba(232,230,224,0.055), ${c.card} 62%)`,
                border: '1px solid rgba(232,230,224,0.14)',
            }}
            gap={0}
        >
            <Stars size={wide ? 15 : 13} />
            <span
                style={{
                    fontSize: wide ? 20 : 15.5,
                    color: c.textPri,
                    fontWeight: 400,
                    letterSpacing: '-0.018em',
                    lineHeight: 1.28,
                    marginTop: wide ? 14 : 13,
                }}
            >
                {review.title}
            </span>
            <Muted
                style={{
                    fontSize: wide ? 14.5 : 12.5,
                    marginTop: wide ? 12 : 9,
                    lineHeight: 1.55,
                    flex: cut ? '0 0 auto' : 1,
                    ...(cut
                        ? {
                              display: '-webkit-box',
                              WebkitLineClamp: clamp,
                              WebkitBoxOrient: 'vertical' as const,
                              overflow: 'hidden',
                          }
                        : null),
                }}
            >
                {review.quote}
            </Muted>
            {cut && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    style={{
                        alignSelf: 'flex-start',
                        marginTop: 8,
                        padding: 0,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: f.ui,
                        fontSize: 12.5,
                        color: ACT,
                        borderBottom: `1px solid ${ACT}55`,
                    }}
                >
                    {t('pw.review.more')}
                </button>
            )}
            <Row
                style={{ gap: 10, marginTop: wide ? 18 : 15, paddingTop: 13, borderTop: hair2, alignItems: 'center' }}
            >
                <div
                    style={{
                        width: 29,
                        height: 29,
                        borderRadius: '50%',
                        flex: '0 0 auto',
                        background: 'rgba(232,230,224,0.07)',
                        border: hair,
                        display: 'grid',
                        placeItems: 'center',
                    }}
                >
                    <span style={{ fontSize: 12, color: c.text, fontWeight: 500 }}>{review.name.slice(0, 1)}</span>
                </div>
                {/* Name and age on one line, what they do underneath: the role is
                    often longer than both put together and would push the age off
                    the card if it shared the row. */}
                <Col style={{ minWidth: 0 }} gap={2}>
                    <Row style={{ gap: 6, alignItems: 'baseline' }}>
                        <span style={{ fontSize: 12.5, color: c.textPri, fontWeight: 400 }}>{review.name}</span>
                        <span style={{ fontSize: 12.5, color: c.muted, fontWeight: 300 }}>· {review.age}</span>
                    </Row>
                    {review.role && <Muted style={{ fontSize: 11.5, lineHeight: 1.35 }}>{review.role}</Muted>}
                </Col>
            </Row>
        </Col>
    );
};

/* ── bottom sheet ─────────────────────────────────────────────────────────── */

/**
 * Modal sheet for the exit ladder. Dims the screen behind it, animates up from
 * the bottom, and traps focus loosely by covering the page — the only ways out
 * are its own two buttons, which is the point of an interception.
 */
export const Sheet: React.FC<{ open: boolean; onDismiss?: () => void; children: React.ReactNode }> = ({
    open,
    onDismiss,
    children,
}) => {
    const reduced = usePrefersReducedMotion();
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
        if (!open) return undefined;
        const id = requestAnimationFrame(() => setMounted(true));

        return () => cancelAnimationFrame(id);
    }, [open]);

    React.useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;

        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 90 }} role="dialog" aria-modal="true">
            <button
                type="button"
                aria-label="Close"
                onClick={onDismiss}
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.76)',
                    border: 'none',
                    cursor: onDismiss ? 'pointer' : 'default',
                    opacity: mounted ? 1 : 0,
                    transition: reduced ? 'none' : 'opacity 380ms',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    maxHeight: '92vh',
                    overflowY: 'auto',
                    background: c.bg,
                    borderTop: hair,
                    borderRadius: '22px 22px 0 0',
                    boxShadow: '0 -24px 60px rgba(0,0,0,0.6)',
                    transform: mounted || reduced ? 'none' : 'translateY(40px) scale(0.985)',
                    opacity: mounted || reduced ? 1 : 0,
                    transition: reduced ? 'none' : 'transform 380ms cubic-bezier(.22,.8,.28,1), opacity 380ms',
                }}
            >
                <div style={{ maxWidth: 430, margin: '0 auto' }}>
                    <Row style={{ justifyContent: 'center', paddingTop: 10 }}>
                        <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(232,230,224,0.18)' }} />
                    </Row>
                    {children}
                </div>
            </div>
        </div>
    );
};

/* ── misc ─────────────────────────────────────────────────────────────────── */

/** A row of pill-shaped reassurances: "no charge today", "cancel any time". */
export const Assurances: React.FC<{ items: string[]; center?: boolean }> = ({ items, center }) => (
    <Row style={{ gap: 9, flexWrap: 'wrap', justifyContent: center ? 'center' : 'flex-start' }}>
        {items.map((t) => (
            <Row key={t} style={{ gap: 7, padding: '7px 12px', borderRadius: 99, border: hair, background: c.cardIn }}>
                <Icon k="check" size={11} color={OK} />
                <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.1em' }}>{t}</Meta>
            </Row>
        ))}
    </Row>
);

/** Countdown pair used by both sticky bars — big mono digits over a label. */
export const Countdown: React.FC<{ label: string; time: string; align?: 'left' | 'right'; size?: number }> = ({
    label,
    time,
    align = 'left',
    size = 20,
}) => (
    <Col style={{ flex: '0 0 auto', alignItems: align === 'right' ? 'flex-end' : 'flex-start' }} gap={2}>
        <span
            style={{
                fontFamily: f.mono,
                fontSize: size,
                fontWeight: 300,
                color: ACT,
                letterSpacing: '-0.03em',
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {time}
        </span>
        <Meta style={{ fontSize: 9, color: c.muted, letterSpacing: '0.1em' }}>{label}</Meta>
    </Col>
);
